/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToCartFunctionalComponent as AddToCartComponent, IAddToCartFailureResult, IAddToCartResources } from './addtocart.component';
import { getUrlSync, IImageSettings } from '@msdyn365-commerce/core';
import { ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import { ProductAvailableQuantity, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, DeliveryMode } from '@msdyn365-commerce-modules/retail-actions';
import { Alert, getTelemetryObject, Heading, ITelemetryContent, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonData,
    IBuyboxErrorBlockProps,
    IBuyboxExtentedProps,
    IBuyboxState,
    IProductDetails
} from './buyboxInterface';
import { getConfigureErrors, getGenericError, getQuantityError } from './utilities/error-utilities';
import { getProductSchedulingMode } from '../../utils/product-selection-mode';

const _getClassNamePrefix = (typeName: string): string => {
    return typeName.toLocaleLowerCase() === 'quickview' ? 'ms-quickView' : 'ms-buybox';
};

export function getBuyBoxInventoryLabel(
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    productDetails?: IProductDetails
): React.ReactElement | undefined {
    const { typeName } = props;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const productAvailableQuantity = props.data.productAvailableQuantity?.result ?? productDetails?.productAvailableQuantity;

    const className = _getClassNamePrefix(typeName);
    if (!productAvailableQuantity || !ArrayExtensions.hasElements(productAvailableQuantity)) {
        return undefined;
    }
    const shippingInventory = productAvailableQuantity.find(shipping => shipping.deliveryType === DeliveryMode.shipping);

    if (shippingInventory) {
        let inventoryClassName: string;
        if (shippingInventory.StockLevelCode) {
            inventoryClassName = `${className}__inventory-label ${className}__inventory-code-${shippingInventory.StockLevelCode.toLowerCase()}`;
        } else {
            inventoryClassName = `${className}__inventory-label`;
        }
        return (
            <div className={`${className}__inventory-info`}>
                <span className={inventoryClassName}>{shippingInventory.StockLevelLabel}</span>
            </div>
        );
    }

    return undefined;
}

export function getBuyboxProductTitle(
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    productDetails?: IProductDetails
): React.ReactElement | undefined {
    const {
        config: { titleHeadingTag = 'h1' },
        typeName
    } = props;
    const className = _getClassNamePrefix(typeName);

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const product = props.data.product?.result ?? productDetails?.product ?? undefined;

    return product && <Heading className={`${className}__product-title`} headingTag={titleHeadingTag} text={product.Name ?? ''} />;
}

export function getBuyboxProductDescription(
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    productDetails?: IProductDetails
): React.ReactElement | undefined {
    const { typeName } = props;
    const className = _getClassNamePrefix(typeName);

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const product = props.data.product?.result ?? productDetails?.product ?? undefined;

    return product && <p className={`${className}__product-description`}>{product.Description}</p>;
}

/**
 * Gets the react node for product unit of measure display.
 * @param {IBuyboxExtentedProps<IBuyboxCommonData>} props The buybox module props.
 * @param {IProductDetails} productDetails The product details to be displyed in buybox.
 * @returns {React.ReactElement} The node representing markup for unit of measure component.
 */
export function getBuyboxProductUnitOfMeasure(
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    productDetails?: IProductDetails
): React.ReactElement | undefined {
    const { typeName } = props;
    const className = _getClassNamePrefix(typeName);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Do not need type check for appsettings
    if (
        props.context.app.config &&
        (!props.context.app.config.unitOfMeasureDisplayType || props.context.app.config.unitOfMeasureDisplayType === 'none')
    ) {
        return undefined;
    }

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const product = props.data.product?.result ?? productDetails?.product;
    if (!product || !product.DefaultUnitOfMeasure) {
        props.telemetry.warning('Missing defult unit of measureor theproduct');
        return undefined;
    }

    return (
        <div className={`${className}__product-unit-of-measure`}>
            <span>{product.DefaultUnitOfMeasure}</span>
        </div>
    );
}

let telemetryContent: ITelemetryContent;

/**
 * On add to cart failed function.
 * @param props -The buybox module props.
 * @param state -The buybox module state.
 * @param callbacks -The buybox module callbacks.
 * @param defaultMinimumKeyInPrice -The default minimum key in price.
 * @param defaultMaximumKeyInPrice -The default maximum key in price.
 * @param cart -The cart state.
 * @param product -The simple product.
 * @param productAvailability -The product available quantity.
 * @returns Update error state.
 */
const onAddToCartFailed = (
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    defaultMinimumKeyInPrice: number,
    defaultMaximumKeyInPrice: number,
    cart: ICartState,
    product: SimpleProduct,
    productAvailability: ProductAvailableQuantity | undefined
) => (result: IAddToCartFailureResult) => {
    let quantityErrorForState: string | undefined;
    let customAmountErrorForState: string | undefined;
    let otherErrorForState: string | undefined = getGenericError(
        result,
        cart,
        props.resources,
        props.context,
        product,
        productAvailability,
        undefined
    );

    if (result.failureReason === 'OUTOFSTOCK') {
        quantityErrorForState = result.failureReason === 'OUTOFSTOCK' ? getQuantityError(result.stockLeft, props.resources) : undefined;
    } else if (
        result.failureReason === 'CARTACTIONFAILED' &&
        result.cartActionResult &&
        (result.cartActionResult.substatus === 'MAXQUANTITY' || result.cartActionResult.substatus === 'QUANTITYLIMITS')
    ) {
        quantityErrorForState = getGenericError(result, cart, props.resources, props.context, product, productAvailability, undefined);
        otherErrorForState = undefined; // Prevent error duplication in otherError and quantityError
    }

    if (result.failureReason === 'INVALIDCUSTOMAMOUNT') {
        if (state.keyInPriceAmount === undefined || state.keyInPriceAmount < (props.config.minimumKeyInPrice || defaultMinimumKeyInPrice)) {
            const formatedAmountLimit = props.context.cultureFormatter.formatCurrency(
                props.config.minimumKeyInPrice || defaultMinimumKeyInPrice
            );
            customAmountErrorForState = props.resources.invalidSmallCustomAmountText.replace('{minAmount}', formatedAmountLimit);
        } else if (state.keyInPriceAmount > (props.config.maximumKeyInPrice || defaultMaximumKeyInPrice)) {
            const formatedAmountLimit = props.context.cultureFormatter.formatCurrency(
                props.config.maximumKeyInPrice || defaultMaximumKeyInPrice
            );
            customAmountErrorForState = props.resources.invalidLargeCustomAmountText.replace('{maxAmount}', formatedAmountLimit);
        }
    }

    callbacks.updateErrorState({
        errorHost: 'ADDTOCART',
        quantityError: quantityErrorForState,
        configureErrors:
            result.failureReason === 'MISSINGDIMENSION'
                ? getConfigureErrors(result.missingDimensions, props.resources, product?.IsGiftCard)
                : {},
        customAmountError: customAmountErrorForState,
        otherError: otherErrorForState
    });
};

/**
 * On Item Add To Cart function.
 * @param typeName -Type name.
 * @param callbacks -Buybox Callbacks.
 * @returns Change modal open.
 */
const onItemAddedToCart = (typeName: string, callbacks: IBuyboxCallbacks) => (result: ICartActionResult) => {
    if (typeName === 'quickview') {
        callbacks.changeModalOpen(false);
    }
};

export function getBuyboxAddToCart(
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    defaultMinimumKeyInPrice: number,
    defaultMaximumKeyInPrice: number,
    productDetails?: IProductDetails,
    hasAvailableProducts?: boolean
): IBuyboxAddToCartViewProps {
    const { id, typeName, context, resources } = props;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const product = props.data.product?.result ?? productDetails?.product;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const cart = props.data.cart?.result ?? productDetails?.cart;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const productAvailableQuantity = props.data.productAvailableQuantity?.result ?? productDetails?.productAvailableQuantity;

    const {
        quantity,
        errorState: { configureErrors, quantityError, customAmountError, otherError, errorHost },
        selectedProduct,
        isUpdatingDimension,
        isUpdatingDeliveryOptions,
        isServiceItem,
        availableEvents,
        availableFringes
    } = state;

    const productAvailability = state.productAvailableQuantity
        ? state.productAvailableQuantity.ProductAvailableQuantity
        : productAvailableQuantity && productAvailableQuantity.length > 0
        ? productAvailableQuantity[0].ProductAvailableQuantity
        : undefined;

    const dialogStrings: IAddToCartResources = {
        goToCartText: resources.buyBoxGoToCartText,
        continueShoppingText: resources.buyBoxContinueShoppingText,
        closeNotificationLabel: resources.closeNotificationLabel,
        headerItemOneText: resources.buyBoxSingleItemText,
        headerItemFormatText: resources.buyBoxMultiLineItemFormatText,
        headerMessageText: resources.buyBoxHeaderMessageText,
        freePriceText: resources.priceFree,
        originalPriceText: resources.originalPriceText,
        currentPriceText: resources.currentPriceText,
        addedQuantityText: resources.addedQuantityText
    };

    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
            lg: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
            xl: { q: 'w=240&h=240&m=6', w: 0, h: 0 }
        },
        lazyload: true
    };

    const isProductQuantityLoading = typeName !== 'quickview' && props.data.productAvailableQuantity.status === 'LOADING';
    if (isProductQuantityLoading) {
        callbacks.changeUpdatingDimension(false);
    }

    const isLoadingDeliveryOptions = typeName !== 'quickview' && props.data.deliveryOptions?.status === 'LOADING';
    if (isLoadingDeliveryOptions && callbacks.changeUpdatingDeliveryOptions) {
        callbacks.changeUpdatingDeliveryOptions(false);
    }

    telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.typeName, props.telemetry);

    // Check if the product delivery mode is electronic
    const emailDeliveryModeCode = props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode;
    // from quickview we are not getting deliveryoptions in props.data hence getting it from quickview state
    const deliveryOptions = typeName === 'quickview' ? state.productDeliveryOptions : props.data.deliveryOptions?.result;
    const isEmailDelivery =
        ArrayExtensions.hasElements(deliveryOptions?.DeliveryOptions) && deliveryOptions!.DeliveryOptions[0].Code === emailDeliveryModeCode;
    const className = _getClassNamePrefix(typeName);
    const mode = getProductSchedulingMode(props.context);

    const disabledBySelectionAvailability =
        mode === 'fringes'
            ? !(availableFringes && availableFringes.length > 0)
            : mode === 'hallTimeslots'
            ? !(availableEvents && availableEvents.length > 0)
            : false;

    const isDisabled = (isUpdatingDimension && typeName === 'quickview') || disabledBySelectionAvailability;

    return {
        ContainerProps: {
            className: `${className}__add-to-cart-container product-add-to-cart`
        },
        button: product && (
            <AddToCartComponent
                addToCartText={resources.addToCartText}
                outOfStockText={resources.outOfStockText}
                navigationUrl={getUrlSync('cart', context.actionContext)}
                quantity={quantity}
                data={{ product, price: state.productPrice }}
                context={context}
                disabled={isDisabled}
                id={id}
                hasAvailableProducts={hasAvailableProducts}
                typeName={typeName}
                onError={onAddToCartFailed(
                    props,
                    state,
                    callbacks,
                    defaultMinimumKeyInPrice,
                    defaultMaximumKeyInPrice,
                    cart!,
                    product,
                    productAvailability
                )}
                getSelectedProduct={selectedProduct}
                productAvailability={productAvailability}
                isProductQuantityLoading={isProductQuantityLoading}
                isUpdatingDimension={isUpdatingDimension}
                changeUpdatingDimension={callbacks.changeUpdatingDimension}
                isLoadingDeliveryOptions={isLoadingDeliveryOptions}
                isUpdatingDeliveryOptions={isUpdatingDeliveryOptions}
                changeUpdatingDeliveryOptions={callbacks.changeUpdatingDeliveryOptions}
                dialogStrings={dialogStrings}
                gridSettings={props.context.request.gridSettings}
                imageSettings={defaultImageSettings}
                telemetryContent={telemetryContent}
                isAddServiceItemToCart={isServiceItem}
                isPriceKeyedIn={state.isPriceKeyedIn}
                customPriceAmount={state.keyInPriceAmount}
                isCustomPriceSelected={state.isCustomPriceSelected}
                maximumKeyInPrice={props.config.maximumKeyInPrice}
                minimumKeyInPrice={props.config.minimumKeyInPrice}
                defaultMinimumKeyInPrice={defaultMinimumKeyInPrice}
                defaultMaximumKeyInPrice={defaultMaximumKeyInPrice}
                isOrderQuantityLimitsFeatureEnabled={_isOrderQuantityLimitsFeatureEnabled(props) || _isChannelLimitsFeatureEnabled(props)}
                isAddEmailDeliveryItemToCart={isEmailDelivery}
                onAdd={onItemAddedToCart(typeName, callbacks)}
                isNavigationToCartPageDisabled={typeName === 'quickview'}
                selectedTimeSlot={state.selectedTimeSlot}
                selectedEvent={state.selectedEvent}
                selectedFringe={state.selectedFringe}
                setTimeSlotError={callbacks.setTimeSlotError}
                setFringeError={callbacks.setTimeSlotError}
            />
        ),
        errorBlock: (
            <BuyboxErrorBlock
                configureErrors={configureErrors}
                quantityError={quantityError}
                customAmountError={customAmountError}
                otherError={otherError}
                resources={resources}
                showError={errorHost === 'ADDTOCART'}
            />
        )
    };
}

export const BuyboxErrorBlock: React.FC<IBuyboxErrorBlockProps> = ({
    showError,
    configureErrors,
    quantityError,
    customAmountError,
    otherError,
    resources
}) => {
    let errorMessages: (string | undefined)[] = [];

    errorMessages = Object.values(configureErrors).filter(message => message !== undefined);

    if (quantityError) {
        errorMessages.push(quantityError);
    }

    if (customAmountError) {
        errorMessages.push(customAmountError);
    }

    if (otherError) {
        errorMessages.push(otherError);
    }

    return (
        <Alert isOpen={showError && errorMessages.length > 0} color='danger' assertive aria-label={resources.buyboxErrorMessageHeader}>
            <div className='msc-alert__header' aria-hidden='true'>
                <span className='msi-exclamation-triangle' />
                <span>{resources.buyboxErrorMessageHeader}</span>
            </div>
            {errorMessages.map((message, index) => {
                return (
                    <div key={index} className='msc-alert__line'>
                        {message}
                    </div>
                );
            })}
        </Alert>
    );
};

export function getQuantityLimitsMessages(
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    state: IBuyboxState
): React.ReactElement | undefined {
    const { resources } = props;

    const { min, max } = state;

    if (!_isOrderQuantityLimitsFeatureEnabled(props) && !_isChannelLimitsFeatureEnabled(props)) {
        return undefined;
    }

    return (
        <Node className='quantity_text_message'>
            {_renderMaxLabel(props, max, resources.maxQuantityText)}
            {_renderMinLabel(props, min, resources.minQuantityText)}
        </Node>
    );
}

export const RetailDefaultOrderQuantityLimitsFeatureName: string = 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature';
export const EcommerceQuantityLimitConfigurationsFeatureName: string =
    'Dynamics.AX.Application.EcommerceQuantityLimitConfigurationsFeature';

const _renderMaxLabel = (
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    max: number | undefined,
    maxQuantityText: string
): React.ReactElement | undefined => {
    const { resources, typeName } = props;
    const className = _getClassNamePrefix(typeName);
    if (max && max !== 0 && max !== Number.MAX_VALUE) {
        return (
            <p className={`${className}__minmax_label_text`}>
                <span className='sr-only'>{resources.informationIconText}</span>
                <i className='msi-InfoCircle' aria-hidden='true' />
                {maxQuantityText.replace('{max}', max.toString())}
            </p>
        );
    }
    return undefined;
};

const _renderMinLabel = (
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    min: number | undefined,
    minQuantityText: string
): JSX.Element | undefined => {
    const { resources, typeName } = props;
    const className = _getClassNamePrefix(typeName);
    if (min && min > 1) {
        return (
            <p className={`${className}__minmax_label_text`}>
                <span className='sr-only'>{resources.informationIconText}</span>
                <i className='msi-InfoCircle' aria-hidden='true' />
                {minQuantityText.replace('{min}', min.toString())}
            </p>
        );
    }
    return undefined;
};

const _isOrderQuantityLimitsFeatureEnabled = (props: IBuyboxExtentedProps<IBuyboxCommonData>): boolean | undefined => {
    const isFeatureEnabledInHq = props.data.featureState.result?.find(
        featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName
    )?.IsEnabled;
    if (!isFeatureEnabledInHq) {
        return false;
    }
    const defaultOrderQuantityLimitsFeatureConfig = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
    if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
        return false;
    }

    if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
        return true;
    }

    const customerInfo = props.data.customerInformation.result;
    return (
        customerInfo &&
        ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
            (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
    );
};

const _isChannelLimitsFeatureEnabled = (props: IBuyboxExtentedProps<IBuyboxCommonData>): boolean => {
    const featureStatuses = props.data.featureState.result;

    const isChannelLimitsFeatureEnabledInHq = featureStatuses?.find(
        featureState => featureState.Name === EcommerceQuantityLimitConfigurationsFeatureName
    )?.IsEnabled;

    return isChannelLimitsFeatureEnabledInHq ?? false;
};
